import React, { useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useSidebar } from "../../content/SidebarContent";
import PageHeader from "../../components/PageHeader";
import Loader from "../../components/Loader";
import { routes } from "../../helpers/constants";
import { reqToGetCouponDetails, reqToUpdateCoupon } from "../../redux/slices.js/promotioncoupons/couponSlice";

// ✅ Validation schema
const validationSchema = Yup.object().shape({
  name: Yup.string().required("Coupon Name is required!"),
  description: Yup.string().required("Description is required!"),
  discount_in_percentage: Yup.number()
    .min(1, "Minimum 1%")
    .max(100, "Maximum 100%")
    .required("Discount % is required!"),
  start_at: Yup.date().required("Start date is required!"),
  end_at: Yup.date()
    .min(Yup.ref("start_at"), "End date must be after start date")
    .required("End date is required!"),
  usage_limit: Yup.number().when("hasUsageLimit", {
    is: true,
    then: (schema) => schema.min(1, "Must be at least 1").required("Usage limit is required!"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const EditCoupons = () => {
  const { setIsSidebarOpen } = useSidebar();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const couponDetails = useSelector((state) => state.promotionCoupons.CouponDetails);
  const loader = useSelector((state) => state.promotionCoupons.loader);

  // ✅ fetch coupon details
  useEffect(() => {
    dispatch(
      reqToGetCouponDetails({
        id: Number(id),
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
  }, [id, dispatch]);

  const formatDate = (isoString) => {
  if (!isoString) return "";
  return new Date(isoString).toISOString().split("T")[0]; // "YYYY-MM-DD"
};


  // ✅ initial values (enableReinitialize so it updates after fetch)
  const initialValues = {
    name: couponDetails?.name || "",
    description: couponDetails?.description || "",
    discount_in_percentage: couponDetails?.discount_in_percentage || "",
    start_at: formatDate(couponDetails?.start_at),
    end_at: formatDate(couponDetails?.end_at),
    hasUsageLimit: couponDetails?.usage_limit != null, // if null → unchecked
    usage_limit: couponDetails?.usage_limit || "",
  };

  // ✅ handle update
  const handleSubmit = (values) => {
    dispatch(
      reqToUpdateCoupon({
        id: Number(id),
        data: {
          name: values.name,
          description: values.description,
          discount_in_percentage: Number(values.discount_in_percentage),
          start_at: values.start_at,
          end_at: values.end_at,
          usage_limit: values.hasUsageLimit ? Number(values.usage_limit) : null,
          action: "edit_info",
        },
        onSuccess: () => navigate(routes.promotionCoupons),
        onFailure: () => {},
      })
    );
  };

  return (
    <>
      {loader && <Loader />}
      <section className="dashboard-page main-layout-page main-layout h-[calc(100vh-50px)]">
        <div className="!p-0">
          <div className="main-layout-wrapper bg-white h-screen flex flex-col sticky z-1 top-0">
            <PageHeader
              title="Edit Coupon"
              description="Update coupon information."
              onOpenSidebar={() => setIsSidebarOpen(true)}
            />

            <div className="flex items-baseline gap-[40px] main-sidebar-wrapper flex-1">
              <div className="flex-1 right-sidebar h-full relative">
                <div className="right-sidebar-contend py-6 px-3 md:px-6 pb-[50px] h-full">
                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    enableReinitialize
                    onSubmit={handleSubmit}
                  >
                    {({ values, setFieldValue }) => (
                      <Form className="signin-form flex flex-col gap-6 h-full">
                        <div className="bg-white box-shadow p-5 space-y-7 rounded-md">
                          <h2 className="text-h4 text-blue">Coupon Information</h2>

                          <div className="grid md:grid-cols-2 gap-6">
                            {/* Name */}
                            <div>
                              <label htmlFor="name">Coupon Name <span>*</span></label>
                              <Field name="name" className="w-full" placeholder="Enter coupon name" />
                              <ErrorMessage name="name" component="div" className="text-red-500 text-sm mt-1" />
                            </div>

                            {/* Description */}
                            <div>
                              <label htmlFor="description">Description <span>*</span></label>
                              <Field name="description" className="w-full" placeholder="Enter description" />
                              <ErrorMessage name="description" component="div" className="text-red-500 text-sm mt-1" />
                            </div>

                            {/* Discount % */}
                            <div>
                              <label htmlFor="discount_in_percentage">Discount (%) <span>*</span></label>
                              <Field type="number" name="discount_in_percentage" className="w-full" />
                              <ErrorMessage name="discount_in_percentage" component="div" className="text-red-500 text-sm mt-1" />
                            </div>

                            {/* Start Date */}
                            <div>
                              <label htmlFor="start_at">Start Date <span>*</span></label>
                              <Field type="date" name="start_at" className="w-full" />
                              <ErrorMessage name="start_at" component="div" className="text-red-500 text-sm mt-1" />
                            </div>

                            {/* End Date */}
                            <div>
                              <label htmlFor="end_at">End Date <span>*</span></label>
                              <Field type="date" name="end_at" className="w-full" />
                              <ErrorMessage name="end_at" component="div" className="text-red-500 text-sm mt-1" />
                            </div>

                            {/* Checkbox + conditional usage limit */}
                            <div className="col-span-2">
                              <label className="flex items-center gap-2">
                                <Field
                                  type="checkbox"
                                  name="hasUsageLimit"
                                  checked={values.hasUsageLimit}
                                  onChange={(e) => {
                                    setFieldValue("hasUsageLimit", e.target.checked);
                                    if (!e.target.checked) setFieldValue("usage_limit", "");
                                  }}
                                />
                                Enable Usage Limit
                              </label>

                              {values.hasUsageLimit && (
                                <div className="mt-2">
                                  <Field
                                    name="usage_limit"
                                    type="number"
                                    placeholder="Enter usage limit"
                                    className="w-full"
                                  />
                                  <ErrorMessage
                                    name="usage_limit"
                                    component="div"
                                    className="text-red-500 text-sm mt-1"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex gap-4 mt-auto">
                          <button type="submit" className="login-btn text-white">
                            Update Coupon
                          </button>
                          <button type="button" className="skip-btn border-blue" onClick={() => navigate(-1)}>
                            Back
                          </button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default EditCoupons;
